import React from 'react';
import { NotificationType, NotificationIconProps } from './types';
import { ReportGeneratedIcon } from './icons/ReportGeneratedIcon';
import { ReferentValidationIcon } from './icons/ReferentValidationIcon';
import { FraudAlertIcon } from './icons/FraudAlertIcon';
import { CandidateRefusalIcon } from './icons/CandidateRefusalIcon';
import { CampaignCompletedIcon } from './icons/CampaignCompletedIcon';
import { CampaignCancelledIcon } from './icons/CampaignCancelledIcon';
import { ReferentCompletedIcon } from './icons/ReferentCompletedIcon';

const ICON_CONFIG: Record<NotificationType, {
  bgColor: string;
  iconColor: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = {
  report_generated: {
    bgColor: 'bg-green-50',
    iconColor: 'text-green-500',
    Icon: ReportGeneratedIcon,
  },
  report_generation_triggered: {
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-500',
    Icon: ReportGeneratedIcon,
  },
  referent_validation: {
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-500',
    Icon: ReferentValidationIcon,
  },
  referent_invited: {
    bgColor: 'bg-teal-50',
    iconColor: 'text-teal-500',
    Icon: ReferentValidationIcon,
  },
  fraud_alert: {
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-500',
    Icon: FraudAlertIcon,
  },
  fraud_alert_unified: {
    bgColor: 'bg-red-50',
    iconColor: 'text-red-600',
    Icon: FraudAlertIcon,
  },
  candidate_refusal: {
    bgColor: 'bg-red-50',
    iconColor: 'text-red-500',
    Icon: CandidateRefusalIcon,
  },
  candidate_accepted: {
    bgColor: 'bg-green-50',
    iconColor: 'text-green-500',
    Icon: ReferentValidationIcon,
  },
  campaign_completed: {
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
    Icon: CampaignCompletedIcon,
  },
  campaign_cancelled: {
    bgColor: 'bg-red-50',
    iconColor: 'text-red-500',
    Icon: CampaignCancelledIcon,
  },
  referent_completed: {
    bgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    Icon: ReferentCompletedIcon,
  },
};

const SIZE_CONFIG = {
  sm: {
    container: 'w-10 h-10 p-2',
    icon: 'w-6 h-6',
  },
  md: {
    container: 'w-12 h-12 p-2.5',
    icon: 'w-7 h-7',
  },
  lg: {
    container: 'w-14 h-14 p-3',
    icon: 'w-8 h-8',
  },
};

export function NotificationIcon({ 
  type, 
  size = 'md',
  className = '' 
}: NotificationIconProps) {
  const config = ICON_CONFIG[type];
  const sizeConfig = SIZE_CONFIG[size];
  
  if (!config) {
    console.warn(`Unknown notification type: ${type}`);
    // Fallback to report_generated icon
    const fallbackConfig = ICON_CONFIG.report_generated;
    const { bgColor, iconColor, Icon } = fallbackConfig;
    
    return (
      <div 
        className={`
          ${sizeConfig.container}
          ${bgColor} 
          rounded-xl 
          inline-flex 
          flex-col 
          justify-center 
          items-center 
          overflow-hidden
          ${className}
        `}
      >
        <Icon className={`${sizeConfig.icon} ${iconColor}`} />
      </div>
    );
  }

  const { bgColor, iconColor, Icon } = config;

  return (
    <div 
      className={`
        ${sizeConfig.container}
        ${bgColor} 
        rounded-xl 
        inline-flex 
        flex-col 
        justify-center 
        items-center 
        overflow-hidden
        ${className}
      `}
    >
      <Icon className={`${sizeConfig.icon} ${iconColor}`} />
    </div>
  );
}
